import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Loader, EmptyState, SpecPill, Pill } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { timeAgo } from "@/lib/labels";

export default function MyJobs() {
  const api = useApi();
  const [jobs, setJobs] = useState(null);
  const load = useCallback(() => api("get", "/jobs/mine").then(setJobs), [api]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await api("delete", `/jobs/${id}`); toast.success("Job deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">Company panel</p><h1 className="mt-2 text-3xl font-extrabold tracking-tight">My jobs</h1></div>
        <Button asChild data-testid="my-jobs-create-button"><Link to="/jobs/new"><Plus size={16} />Post a job</Link></Button>
      </div>
      <div className="mt-8">
        {jobs === null ? <Loader /> : jobs.length === 0 ? (
          <EmptyState testId="my-jobs-empty" title="You have no jobs yet" text="Publish your first job so technicians can apply." />
        ) : (
          <div className="panel divide-y divide-slate-100">
            {jobs.map((j) => (
              <div key={j.id} data-testid={`my-job-row-${j.id}`} className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <Link to={`/jobs/${j.id}`} className="font-bold text-slate-900 hover:text-sky-700">{j.title}</Link>
                  <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                    <SpecPill spec={j.specialization} />
                    {j.active ? <Pill cls="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Pill> : <Pill cls="bg-slate-100 text-slate-500">Inactive</Pill>}
                    <span className="font-mono text-[11px] text-slate-400 ml-1">{timeAgo(j.created_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button asChild size="sm" data-testid={`my-job-applicants-${j.id}`}><Link to={`/my-jobs/${j.id}/applicants`}><Users size={14} />Candidates ({j.applicants_count})</Link></Button>
                  <Button asChild size="sm" variant="outline" data-testid={`my-job-edit-${j.id}`}><Link to={`/jobs/${j.id}/edit`}><Pencil size={14} /></Link></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`my-job-delete-${j.id}`}><Trash2 size={14} /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete this job?</AlertDialogTitle><AlertDialogDescription>All its applications will be removed too.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-job" onClick={() => remove(j.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
